import { SupabaseClient } from "@supabase/supabase-js";
import { HotelChainRep } from "@/lib/types/hotel";

import { GLOBAL_ORG_ID } from "./hotel-chain-rep-precedence";

export { GLOBAL_ORG_ID };

/** Maps a raw `hotel_chain_reps` row (with joined chain) into a `HotelChainRep`. */
function mapRep(rep: any): HotelChainRep {
  return { ...rep, chain_name: rep.venue_chains?.chain_name || null };
}

/** Removes reps that share an email with an earlier rep in the list. */
function dedupeByEmail(reps: HotelChainRep[]): HotelChainRep[] {
  return reps.filter((rep, index, self) => index === self.findIndex((r) => r.email === rep.email));
}

/**
 * Resolves the preferred hotel chain reps to CC on an RFP for a given chain.
 *
 * A customer org's own reps take precedence over the global Nowadays org's
 * (`org_id === 999`), which are used only when the customer org has none. Both
 * org tiers are read in a single query filtered to `preferred_rep = true`, then
 * partitioned in memory and deduplicated by email.
 *
 * @param supabase - Authenticated or service-role Supabase client.
 * @param chainId - The hotel chain to resolve reps for; no chain returns `[]`.
 * @param orgId - The event's org; org-specific reps win, else global (999) reps.
 * @returns The preferred chain reps (with `chain_name`), or `[]` when none.
 */
export async function getPreferredChainRep(
  supabase: SupabaseClient,
  chainId: number | null | undefined,
  orgId?: number | null
): Promise<HotelChainRep[]> {
  if (!chainId) {
    return [];
  }

  // A real customer org can have its own reps that take precedence over the
  // global org's; otherwise we only consult the global org.
  const hasCustomerOrg = orgId !== null && orgId !== undefined && orgId !== GLOBAL_ORG_ID;
  const orgIds = hasCustomerOrg ? [orgId, GLOBAL_ORG_ID] : [GLOBAL_ORG_ID];

  const { data, error } = await supabase
    .schema("nowadays")
    .from("hotel_chain_reps")
    .select(
      `
      *,
      venue_chains:chain_id (
        chain_name
      )
    `
    )
    .eq("chain_id", chainId)
    .eq("preferred_rep", true)
    .in("org_id", orgIds);

  if (error) {
    console.error("Error fetching chain rep:", error);
    return [];
  }

  if (!data || data.length === 0) {
    return [];
  }

  // Prefer the customer org's own reps; fall back to the global org's only when
  // the customer org has none.
  const reps = data.map(mapRep);
  const orgSpecific = hasCustomerOrg ? reps.filter((rep) => rep.org_id === orgId) : [];
  const selected = orgSpecific.length > 0 ? orgSpecific : reps.filter((rep) => rep.org_id === GLOBAL_ORG_ID);

  return dedupeByEmail(selected);
}
